import { useForm } from '@tanstack/react-form';
import { AnimatePresence, LazyMotion, domAnimation, m } from 'motion/react';
import { lazy, useCallback, useState } from 'react';
import { boolean, object, string, type infer as ZodInfer } from 'zod';
const Snippet = lazy(() => import('../../components/Snippet'));

const questions = [
  {
    name: 'answer1' as const,
    question: 'Hur många världsrekord har Kia inskrivna i Guiness rekordbok?',
    options: [
      { label: '1', value: '1', text: '2' },
      { label: 'X', value: 'X', text: '4' },
      { label: '2', value: '2', text: '6' },
    ],
  },
  {
    name: 'answer2' as const,
    question: 'Hur många nya fordonstekniker behövs i Sverige inom tre år?',
    options: [
      { label: '1', value: '1', text: '6 500 st' },
      { label: 'X', value: 'X', text: '650 st' },
      { label: '2', value: '2', text: '65 000 st' },
    ],
  },
  {
    name: 'answer3' as const,
    question:
      'Kia kommer från Sydkorea. Vilken populär Netflix-serie kommer från samma land?',
    options: [
      { label: '1', value: '1', text: 'Love is blind' },
      { label: 'X', value: 'X', text: 'Squid Game' },
      { label: '2', value: '2', text: 'Bridgerton' },
    ],
  },
];

const schema = object({
  answer1: string().min(1, 'Välj ett svar'),
  answer2: string().min(1, 'Välj ett svar'),
  answer3: string().min(1, 'Välj ett svar'),
  motivation: string().min(1, 'Motivering krävs').max(255, 'Max 255 tecken'),
  name: string().min(1, 'Namn krävs'),
  phone: string().regex(
    /^(\+46|0)7[0-9\s\-]{7,9}$/,
    'Ange ett giltigt mobilnummer',
  ),
  email: string().optional(),
  acceptTerms: boolean().refine((v) => v === true, {
    message: 'Du måste godkänna tävlingsvillkoren',
  }),
});

type FormValues = ZodInfer<typeof schema>;

const termsText = `Genom att delta i tävlingen godkänner du följande villkor. Tävlingen arrangeras av Kia Sweden AB och för att delta ska du besvara tävlingsfrågorna samt lämna en motivering enligt anvisningarna. Anställda hos Kia Sweden AB, samt deras närstående, får inte delta i tävlingen. Vinnaren utses av en jury baserat på korrekta svar och bästa motivering, och juryns beslut kan inte överklagas. Eventuell vinst kan inte bytas mot kontanter eller överlåtas till annan person. Vinnaren dras i samband med yrkes-SM och kontaktas via de kontaktuppgifter som angivits i samband med tävlingsbidraget, och om vinnaren inte svarar förbehåller vi oss rätten att utse en ny vinnare. Eventuell vinstskatt eller andra kostnader som kan uppstå i samband med vinsten betalas av vinnaren. Genom att delta godkänner du att dina personuppgifter behandlas i syfte att administrera tävlingen i enlighet med gällande dataskyddslagstiftning.`;

function FieldError({ errors }: { errors: unknown[] }) {
  if (!errors.length) return null;
  const message =
    (errors[0] as { message?: string })?.message ?? String(errors[0]);
  return (
    <p role="alert" aria-live="polite" className="text-red-500 text-sm mt-2">
      {message}
    </p>
  );
}

const TavlingYrkesSM = () => {
  const [submitted, setSubmitted] = useState(false);
  const scrollToSubmitted = useCallback((node: HTMLDivElement | null) => {
    node?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  const form = useForm({
    defaultValues: {
      answer1: '',
      answer2: '',
      answer3: '',
      motivation: '',
      name: '',
      phone: '',
      email: '',
      acceptTerms: false,
    } as FormValues,
    onSubmit: async ({ value }) => {
      if (value.email && value.email.length > 0) {
        //honeypot field
        setSubmitted(true);
        return;
      }
      const res = await fetch('https://kia-nodemailer.vercel.app/api/contest', {
        method: 'POST',
        headers: {
          Accept: 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ values: value }),
      });

      if (!res.ok) {
        throw new Error('Något gick fel, vänligen försök igen senare.');
      }
      setSubmitted(true);
    },
    validators: {
      onSubmit: schema,
    },
  });
  if (submitted) {
    return (
      <LazyMotion features={domAnimation}>
        <m.div
          ref={scrollToSubmitted}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-screen-kia-sm mx-auto px-4 py-24 text-center"
        >
          <div className="inline-block border border-black px-3 py-1 text-sm mb-6">
            Skickat
          </div>
          <h2 className="text-4xl md:text-5xl font-medium mb-6 leading-tight">
            Tack!
          </h2>
          <p className="max-w-prose mx-auto text-base font-light leading-relaxed text-gray-600">
            Håll koll på din mobil. Vi kommer meddela vinnarna under dagen för
            att de ska kunna hämta upp priset.
          </p>
          <button
            type="reset"
            onClick={() => {
              setSubmitted(false);
              form.reset();
            }}
            className="mt-8 border border-[#05141F] text-[#05141F] px-8 py-3 text-base font-medium hover:bg-[#05141F] hover:text-white transition-colors"
          >
            Skicka in nytt bidrag
          </button>
        </m.div>
      </LazyMotion>
    );
  }

  return (
    <LazyMotion features={domAnimation}>
      <div>
        <div className="bg-[#05141F] text-white px-4 py-14 md:py-20">
          <div className="max-w-screen-kia-sm mx-auto">
            <m.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="inline-block border border-white/30 px-3 py-1 text-sm mb-6"
            >
              Tävling
            </m.div>
            <m.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.1,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="text-4xl md:text-6xl font-medium leading-tight mb-5"
            >
              Tävla med Kia
            </m.h1>
            <m.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.2,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="text-base font-light text-white/70 max-w-prose leading-relaxed"
            >
              Svara rätt på tre frågor och tävla om fina priser (värde upp till
              400 kr/st) från Kia.
            </m.p>
          </div>
        </div>

        <section className="max-w-screen-kia-sm mx-auto px-4 py-14">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="flex flex-col gap-14"
          >
            {/* Questions */}
            <div className="flex flex-col gap-12">
              {questions.map((q, qi) => (
                <m.div
                  key={q.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: 0.15 + qi * 0.1,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  <form.Field
                    name={q.name}
                    children={(field) => (
                      <fieldset>
                        <legend className="mb-5">
                          <span className="block text-sm font-light text-gray-400 mb-1">
                            Fråga {qi + 1}
                          </span>
                          <span className="block text-lg md:text-xl font-medium leading-snug">
                            {q.question}
                          </span>
                        </legend>

                        <div className="grid md:grid-cols-3 gap-3">
                          {q.options.map((opt) => {
                            const isSelected = field.state.value === opt.value;
                            const hasError = field.state.meta.errors.length > 0;
                            return (
                              <label
                                key={opt.value}
                                className={`
                                  relative flex flex-col items-center justify-between
                                  gap-4 p-4 md:p-5 cursor-pointer select-none
                                  border transition-all duration-200
                                  rounded-sm
                                  ${
                                    isSelected
                                      ? 'border-[#05141F] bg-[#05141F] text-white'
                                      : hasError
                                        ? 'border-red-300 bg-red-50/40 hover:border-red-400'
                                        : 'border-gray-200 bg-[#F7F7F7] hover:border-gray-400 hover:bg-white'
                                  }
                                `}
                              >
                                <span
                                  className={`text-base md:text-lg font-medium text-center leading-snug ${
                                    isSelected ? 'text-white' : 'text-[#05141F]'
                                  }`}
                                >
                                  {opt.text}
                                </span>
                                <input
                                  type="radio"
                                  className="sr-only"
                                  name={field.name}
                                  value={opt.value}
                                  checked={isSelected}
                                  onBlur={field.handleBlur}
                                  onChange={() => field.handleChange(opt.value)}
                                />
                              </label>
                            );
                          })}
                        </div>

                        <FieldError errors={field.state.meta.errors} />
                      </fieldset>
                    )}
                  />
                </m.div>
              ))}
            </div>

            <m.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: 0.45,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              <form.Field
                name="motivation"
                children={(field) => (
                  <div>
                    <label
                      className="block text-base font-medium mb-2"
                      htmlFor={field.name}
                    >
                      Motivera med max två meningar varför just du ska vinna
                    </label>
                    <textarea
                      id={field.name}
                      rows={4}
                      className={`w-full border resize-none border-kia-gray rounded px-4 py-3 text-base focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-colors ${
                        field.state.meta.errors.length
                          ? 'border-red-400 focus:border-red-600'
                          : 'border-gray-200 focus:border-[#05141F] focus:ring-1 focus:ring-[#05141F]'
                      }`}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                    <FieldError errors={field.state.meta.errors} />
                  </div>
                )}
              />
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <form.Field
                  name="name"
                  children={(field) => (
                    <div>
                      <label
                        className="block text-base font-medium mb-2"
                        htmlFor={field.name}
                      >
                        Namn
                      </label>
                      <input
                        type="text"
                        id={field.name}
                        autoComplete="name"
                        className={`w-full border border-kia-gray h-12 rounded px-4 py-3 text-base focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-colors ${
                          field.state.meta.errors.length
                            ? 'border-red-400 focus:border-red-600'
                            : 'border-gray-200 focus:border-[#05141F] focus:ring-1 focus:ring-[#05141F]'
                        }`}
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                      <FieldError errors={field.state.meta.errors} />
                    </div>
                  )}
                />
                <form.Field
                  name="phone"
                  children={(field) => (
                    <div>
                      <label
                        className="block text-base font-medium mb-2"
                        htmlFor={field.name}
                      >
                        Mobiltelefon
                      </label>
                      <input
                        type="tel"
                        id={field.name}
                        autoComplete="tel"
                        className={`w-full border border-kia-gray h-12 rounded px-4 py-3 text-base focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-colors ${
                          field.state.meta.errors.length
                            ? 'border-red-400 focus:border-red-600'
                            : 'border-gray-200 focus:border-[#05141F] focus:ring-1 focus:ring-[#05141F]'
                        }`}
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                      <FieldError errors={field.state.meta.errors} />
                    </div>
                  )}
                />
              </div>
            </m.div>
            {/* honeypot */}
            <form.Field
              name="email"
              children={(field) => (
                <div
                  aria-hidden="true"
                  className="absolute -left-[9999px] w-px h-px overflow-hidden opacity-0 pointer-events-none"
                >
                  <label htmlFor={field.name}>Email</label>
                  <input
                    type="text"
                    id={field.name}
                    autoComplete="off"
                    tabIndex={-1}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            />
            <m.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: 0.55,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="border border-gray-200 p-6"
            >
              <h3 className="text-base font-semibold mb-4">Tävlingsvillkor</h3>
              <p className="text-sm font-light text-gray-600 leading-relaxed mb-5">
                {termsText}
              </p>
              <form.Field
                name="acceptTerms"
                children={(field) => (
                  <div>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-0.5 flex-shrink-0"
                        checked={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.checked)}
                      />
                      <span className="text-base font-light">
                        Jag godkänner tävlingsvillkoren
                      </span>
                    </label>
                    <FieldError errors={field.state.meta.errors} />
                  </div>
                )}
              />
            </m.div>

            <m.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: 0.6,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="flex flex-col gap-3"
            >
              <form.Subscribe
                selector={(state) => [state.errors]}
                children={([errors]) => (
                  <AnimatePresence>
                    {errors.length > 0 && (
                      <m.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        role="alert"
                        aria-live="polite"
                        className="text-red-500 text-sm"
                      >
                        Vänligen kontrollera att alla fält är korrekt ifyllda.
                      </m.p>
                    )}
                  </AnimatePresence>
                )}
              />
              <form.Subscribe
                selector={(state) => [state.canSubmit, state.isSubmitting]}
                children={([canSubmit, isSubmitting]) => (
                  <>
                    <button
                      type="submit"
                      disabled={!canSubmit}
                      className="bg-[#05141F] text-white px-8 py-4 text-base font-medium hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed self-start"
                    >
                      {isSubmitting ? 'Skickar…' : 'Skicka in tävlingsbidrag'}
                    </button>
                  </>
                )}
              />
            </m.div>
          </form>
        </section>
      </div>
      <Snippet page="tavling-yrkes-sm" />
    </LazyMotion>
  );
};

export default TavlingYrkesSM;
